import { themedConfirm } from '../../lib/themed-dialogs';
// DBHost Module — správa standalone MariaDB/PostgreSQL kontejnerů
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../../lib/mcp';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  LifecycleControls,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '../../components/ui';
import { NodeSelector } from '../../components/NodeSelector';
import { DbInstancePanel } from '../../components/DbInstancePanel';
import {
  AlertCircle,
  Check,
  Copy,
  Database,
  Eye,
  EyeOff,
  Network,
  Play,
  Plus,
  Server,
  Settings,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

type DbEngine = 'postgres' | 'mariadb';

interface DbInstance {
  id: string;
  name: string;
  engine: DbEngine;
  version: string;
  port: number | null;
  memory_limit: string;
  created_at: string;
  container_status: 'running' | 'stopped' | 'missing';
  database_count: number;
  credential_count: number;
}

interface ConnectionInfo {
  ok: boolean;
  instance: string;
  engine: DbEngine;
  host: string;
  port: number;
  root_user: string;
  root_password: string;
  connection_strings: Record<string, string>;
  credentials: { username: string; db_name: string | null; connection_string: string }[];
}

interface DbInstanceStatus {
  ok: boolean;
  instance: {
    id: string;
    name: string;
    engine: DbEngine;
    version: string;
    container_status: 'running' | 'stopped' | 'missing';
    port: number | null;
    memory_limit: string;
    disk: string;
  };
  databases: { db_name: string; owner: string | null }[];
  credentials: { username: string; db_name: string | null; privileges: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const DEFAULT_VERSIONS: Record<DbEngine, string[]> = {
  postgres: ['16', '15', '14', '13'],
  mariadb: ['11.4', '10.11', '10.6', '10.5'],
};

const INTERNAL_PORT: Record<DbEngine, number> = {
  postgres: 5432,
  mariadb: 3306,
};

function buildConnStr(
  engine: DbEngine,
  user: string,
  password: string,
  host: string,
  port: number,
  db: string,
): string {
  const proto = engine === 'postgres' ? 'postgresql' : 'mysql';
  return `${proto}://${user}:${password}@${host}:${port}/${db}`;
}

function copyText(text: string, copiedMsg: string) {
  navigator.clipboard.writeText(text).then(() => toast.success(copiedMsg)).catch(() => {});
}

// ── Instance Card ─────────────────────────────────────────────────────────────

function InstanceCard({
  inst,
  selected,
  onSelect,
  onAction,
}: {
  inst: DbInstance;
  selected: boolean;
  onSelect: () => void;
  onAction: (action: 'start' | 'stop' | 'restart' | 'delete') => void;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<string | undefined>();
  const qc = useQueryClient();

  const manageMut = useMutation({
    mutationFn: (action: string) => {
      setPending(action);
      return mcpCall('dbhost.manage', { name: inst.name, action });
    },
    onSettled: () => setPending(undefined),
    onSuccess: (_, action) => {
      toast.success(`${inst.name}: ${action}`);
      qc.invalidateQueries({ queryKey: ['dbhost-list'] });
      qc.invalidateQueries({ queryKey: ['dbhost-status', inst.name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: () => mcpCall('dbhost.delete', { name: inst.name }),
    onSuccess: () => {
      toast.success(t('dbhost.instanceDeleted', { name: inst.name }));
      qc.invalidateQueries({ queryKey: ['dbhost-list'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const running = inst.container_status === 'running';

  return (
    <div
      onClick={onSelect}
      className={`group cursor-pointer border rounded-2xl p-4 transition-all ${
        selected
          ? 'border-blue-500/40 bg-blue-950/10'
          : 'border-gray-800 hover:border-gray-700 bg-gray-900/20 hover:bg-gray-900/40'
      }`}
    >
      <div className='flex items-start justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <StatusDot
            status={inst.container_status === 'missing' ? 'error' : inst.container_status}
            size='md'
            className='flex-shrink-0'
          />
          <span className='font-semibold text-white text-sm'>{inst.name}</span>
        </div>
        <Badge variant={inst.engine === 'postgres' ? 'blue' : 'yellow'}>
          {inst.engine === 'postgres' ? 'PostgreSQL' : 'MariaDB'}
        </Badge>
      </div>

      {inst.port && (
        <div className='flex items-center gap-2 mb-3 bg-gray-900/60 rounded-lg px-3 py-1.5'>
          <span className='text-[10px] text-gray-500 uppercase tracking-wide'>{t('dbhost.port')}</span>
          <code className='text-xs font-mono text-emerald-400 flex-1'>:{inst.port}</code>
          <button
            type='button'
            onClick={async (e) => {
              e.stopPropagation();
              copyText(String(inst.port), t('dbhost.copied'));
            }}
            className='text-gray-600 hover:text-white transition-colors'
            title={t('dbhost.copyPort')}
          >
            <Copy size={12} />
          </button>
        </div>
      )}

      <div className='flex items-center gap-3 text-xs text-gray-500 mb-3'>
        <span>v{inst.version}</span>
        <span>{inst.memory_limit}</span>
        <span>{t('dbhost.dbCount', { count: inst.database_count })}</span>
        <span>{t('dbhost.userCount', { count: inst.credential_count })}</span>
      </div>

      <div className='opacity-0 group-hover:opacity-100 transition-opacity'>
        <LifecycleControls.Row
          name={inst.name}
          running={running}
          pending={pending}
          onAction={async (_name, action) => {
            if (action === 'delete') {
              if (await themedConfirm(t('dbhost.confirmDelete', { name: inst.name }))) {
                deleteMut.mutate();
              }
            } else {
              manageMut.mutate(action);
              onAction(action);
            }
          }}
        />
      </div>
    </div>
  );
}

// ── Instance Detail ───────────────────────────────────────────────────────────

function InstanceDetail(
  { name, engine, onRefresh }: { name: string; engine: DbEngine; onRefresh: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showRootPass, setShowRootPass] = useState(false);
  const [portModalOpen, setPortModalOpen] = useState(false);

  const statusQ = useQuery({
    queryKey: ['dbhost-status', name],
    queryFn: () => mcpCall<DbInstanceStatus>('dbhost.status', { name }),
    refetchInterval: 15_000,
  });

  const connQ = useQuery({
    queryKey: ['dbhost-connections', name],
    queryFn: () => mcpCall<ConnectionInfo>('dbhost.connections', { instance: name }),
  });

  const manageMut = useMutation({
    mutationFn: (action: string) => mcpCall('dbhost.manage', { name, action }),
    onSuccess: (_, action) => {
      toast.success(t('dbhost.actionDone', { action }));
      qc.invalidateQueries({ queryKey: ['dbhost-status', name] });
      qc.invalidateQueries({ queryKey: ['dbhost-list'] });
      qc.invalidateQueries({ queryKey: ['dbhost-connections', name] });
      onRefresh();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const data = statusQ.data;
  const conn = connQ.data;
  const inst = data?.instance;
  const running = inst?.container_status === 'running';

  if (statusQ.isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-8 bg-gray-800/40 rounded-xl animate-pulse' />
        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className='h-24 bg-gray-800/40 rounded-xl animate-pulse' />
          ))}
        </div>
        <div className='h-48 bg-gray-800/40 rounded-xl animate-pulse' />
      </div>
    );
  }

  if (!data?.ok || !inst) return null;

  return (
    <div className='space-y-4'>
      {/* Header */}
      <div className='flex items-center gap-3 flex-wrap'>
        <StatusDot
          status={inst.container_status === 'missing' ? 'error' : inst.container_status}
          size='md'
          className='flex-shrink-0'
        />
        <h2 className='text-lg font-bold text-white'>{name}</h2>
        <Badge variant={engine === 'postgres' ? 'blue' : 'yellow'}>
          {engine === 'postgres' ? 'PostgreSQL' : 'MariaDB'} v{inst.version}
        </Badge>
        <div className='ml-auto'>
          <LifecycleControls.Bar
            name={name}
            running={running}
            loading={manageMut.isPending}
            onAction={(action) => manageMut.mutate(action)}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <StatCard
          label={t('dbhost.status')}
          value={inst.container_status}
          variant={running ? 'success' : 'default'}
        />
        <button
          type='button'
          onClick={() => setPortModalOpen(true)}
          className='text-left'
          title={t('dbhost.changeExternalPort')}
        >
          <StatCard
            label={t('dbhost.externalPortClickToChange')}
            value={inst.port ? `:${inst.port}` : '—'}
          />
        </button>
        <StatCard label={t('dbhost.memory')} value={inst.memory_limit} />
        <StatCard label={t('dbhost.disk')} value={inst.disk} />
      </div>

      {/* Connection — 4 perspektivy */}
      <Card>
        <CardHeader>
          <CardTitle icon={Network}>{t('dbhost.connectionPerspectives')}</CardTitle>
        </CardHeader>
        <CardContent className='p-4'>
          {connQ.isLoading && (
            <div className='space-y-2'>
              <div className='h-8 bg-gray-800/40 rounded-xl animate-pulse' />
              <div className='h-24 bg-gray-800/40 rounded-xl animate-pulse' />
            </div>
          )}

          {conn && conn.ok && (
            <ConnectionPerspectives
              instance={name}
              engine={engine}
              externalPort={inst.port ?? conn.port}
              rootUser={conn.root_user}
              rootPassword={conn.root_password}
              credentials={conn.credentials}
              showRootPass={showRootPass}
              onToggleRootPass={() => setShowRootPass((s) => !s)}
            />
          )}
        </CardContent>
      </Card>

      {/* Config Editor */}
      <Card>
        <CardHeader>
          <CardTitle icon={Settings}>{t('dbhost.serverConfig')}</CardTitle>
        </CardHeader>
        <CardContent className='p-4'>
          <ServerConfigEditor instance={name} inst={statusQ.data?.instance} />
        </CardContent>
      </Card>

      {/* DbInstancePanel */}
      <DbInstancePanel
        instance={name}
        engine={engine}
        dbCount={data.databases?.length}
        credCount={data.credentials?.length}
      />

      {/* Port edit modal */}
      <ChangePortModal
        open={portModalOpen}
        onClose={() => setPortModalOpen(false)}
        instance={name}
        currentPort={inst.port}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ['dbhost-status', name] });
          qc.invalidateQueries({ queryKey: ['dbhost-connections', name] });
          qc.invalidateQueries({ queryKey: ['dbhost-list'] });
          onRefresh();
        }}
      />
    </div>
  );
}

// ── ServerConfigEditor ───────────────────────────────────────────────────────

function ServerConfigEditor({ instance, inst }: { instance: string; inst?: DbInstance }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [search, setSearch] = useState('');

  const statusQ = useQuery({
    queryKey: ['dbhost-status', instance],
    queryFn: () => mcpCall<DbInstanceStatus>('dbhost.status', { name: instance }),
  });

  const configQ = useQuery({
    queryKey: ['dbhost-config', instance],
    queryFn: async () => {
      const result = await mcpCall<{ ok: boolean; output: string; config: Record<string, unknown> }>(
        'dbhost.config', { instance },
      );
      return result;
    },
  });

  const variablesQ = useQuery({
    queryKey: ['mariadb-variables', instance],
    queryFn: async () => {
      const result = await mcpCall<{ ok: boolean; output: string; variables: Record<string, unknown> }>(
        'dbhost.mariadb_variables', { instance },
      );
      return result;
    },
    enabled: statusQ.isSuccess,
  });

  const configMut = useMutation({
    mutationFn: (newConfig: Record<string, unknown>) =>
      mcpCall('dbhost.config', { instance, ...newConfig }),
    onSuccess: () => {
      toast.success(t('dbhost.configSaved'));
      qc.invalidateQueries({ queryKey: ['dbhost-status', instance] });
      qc.invalidateQueries({ queryKey: ['dbhost-config', instance] });
      setConfig({});
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const memoryOptions = ['256m', '512m', '1g', '2g', '4g'];
  const cpuOptions = ['0.5', '1', '1.5', '2', '2.5', '3', '4'];

  const filteredMemory = memoryOptions.filter(
    (m) => m.toLowerCase().includes(search.toLowerCase()) || search === '',
  );
  const filteredCpu = cpuOptions.filter(
    (c) => c.toLowerCase().includes(search.toLowerCase()) || search === '',
  );

  const currentConfig = configQ.data?.config ?? {};
  const mysqlVars = variablesQ.data?.variables as Record<string, Record<string, string>> ?? {};
  
  const hasChanges = Object.keys(config).length > 0;

  if (statusQ.isLoading || configQ.isLoading || variablesQ.isLoading) {
    return (
      <div className='space-y-3'>
        <div className='grid grid-cols-2 gap-3'>
          <div className='h-20 bg-gray-800/40 rounded-xl animate-pulse' />
          <div className='h-20 bg-gray-800/40 rounded-xl animate-pulse' />
        </div>
        <div className='h-32 bg-gray-800/40 rounded-xl animate-pulse' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Quick stats */}
      <StatsGrid columns={2}>
        <StatCard
          label='Memory'
          value={(currentConfig.memory_limit as string) ?? '—'}
          icon={Server}
        />
        <StatCard label='CPU' value={(currentConfig.cpu_limit as string) ?? '—'} icon={Server} />
      </StatsGrid>

      {/* Help text */}
      <div className='bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-xs text-blue-200'>
        <div className='font-semibold mb-1'>{t('dbhost.howItWorks')}</div>
        <ul className='list-disc pl-4 space-y-1 text-blue-100'>
          <li><strong>Memory/CPU:</strong> {t('dbhost.helpMemoryCpu')}</li>
          <li><strong>{t('dbhost.helpMysqlLabel')}</strong> {t('dbhost.helpMysql')}</li>
          <li><strong>{t('dbhost.helpRestartLabel')}</strong> {t('dbhost.helpRestart')}</li>
        </ul>
      </div>

      {/* Search filter */}
      <div>
        <div className='text-xs text-gray-400 mb-2 flex items-center gap-2'>
          <Settings size={12} className='text-purple-400' />
          <span>{t('dbhost.filterSettings')}</span>
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('dbhost.filterPlaceholder')}
          size='md'
        />
      </div>

      {/* Tabs */}
      <Tabs defaultTab='mysql'>
        <TabList>
          <Tab id='mysql' icon={<Database size={15} />}>{t('dbhost.tabMysql')}</Tab>
          <Tab id='memory' icon={<Server size={15} />}>{t('dbhost.tabMemoryCpu')}</Tab>
          <Tab id='network' icon={<Network size={15} />}>{t('dbhost.tabNetwork')}</Tab>
        </TabList>

        {/* MySQL Settings - dynamic from mariadb_variables */}
        <TabPanel id='mysql'>
          <div className='bg-gray-900/40 rounded-xl p-4 border border-gray-800 space-y-3 max-h-[400px] overflow-y-auto'>
            {Object.entries(mysqlVars).map(([category, vars]) => (
              <div key={category}>
                <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 capitalize'>
                  {category}
                </h4>
                {Object.entries(vars as Record<string, string>)
                  .filter(([k]) => k.toLowerCase().includes(search.toLowerCase()))
                  .map(([key, value]) => (
                    <div key={key} className='flex items-center gap-2 text-sm mb-2'>
                      <div className='flex-1 min-w-0'>
                        <span className='text-white font-mono text-xs block truncate' title={key}>
                          {key}
                        </span>
                        <span className='text-gray-500 text-[10px] truncate' title={value}>
                          {value}
                        </span>
                      </div>
                      <div className='w-32 flex-shrink-0'>
                        <Input
                          size='sm'
                          type='text'
                          value={config[key] as string ?? value}
                          onChange={(e) =>
                            setConfig((prev) => ({ ...prev, [key]: e.target.value }))}
                          className='font-mono'
                        />
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </TabPanel>

        {/* Memory/CPU Settings */}
        <TabPanel id='memory'>
          <div className='space-y-4'>
            <div>
              <Label>{t('dbhost.memoryLimit')}</Label>
              <Select
                size='sm'
                options={filteredMemory.map((o) => ({ value: o, label: o }))}
                value={config.memory_limit as string ?? currentConfig.memory_limit}
                onChange={(e) => setConfig((prev) => ({ ...prev, memory_limit: e.target.value }))}
              />
              <div className='text-[10px] text-gray-500 mt-1'>{t('dbhost.dockerMemoryLimit')}</div>
            </div>
            <div>
              <Label>{t('dbhost.cpuLimit')}</Label>
              <Select
                size='sm'
                options={filteredCpu.map((o) => ({ value: o, label: o }))}
                value={config.cpu_limit as string ?? currentConfig.cpu_limit}
                onChange={(e) => setConfig((prev) => ({ ...prev, cpu_limit: e.target.value }))}
              />
              <div className='text-[10px] text-gray-500 mt-1'>{t('dbhost.dockerCpuLimit')}</div>
            </div>
          </div>
        </TabPanel>

        {/* Network Settings */}
        <TabPanel id='network'>
          <div className='bg-gray-900/40 rounded-xl p-4 border border-gray-800 space-y-3'>
            <div className='text-sm text-gray-400'>
              <p>
                {t('dbhost.containerPort')} <code className='text-blue-400'>:{inst?.port}</code>
              </p>
              <p className='mt-1'>{t('dbhost.networkEditHint')}</p>
            </div>
          </div>
        </TabPanel>
      </Tabs>

      {/* Restart toggle */}
      <div className='flex items-center justify-between bg-gray-900/40 rounded-xl p-3 border border-gray-800'>
        <div className='flex items-center gap-3'>
          <div className='w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center'>
            <Play size={15} className='text-blue-400' />
          </div>
          <div>
            <div className='text-sm font-medium text-white'>{t('dbhost.restartAfterChange')}</div>
            <div className='text-[10px] text-gray-500'>{t('dbhost.restartAuto')}</div>
          </div>
        </div>
        <Toggle
          value={config.restart as boolean ?? true}
          onSave={(v) => setConfig((prev) => ({ ...prev, restart: v }))}
        />
      </div>
      {/* Save button */}
      <Button
        variant='primary'
        size='md'
        icon={<Check size={15} />}
        loading={configMut.isPending}
        onClick={async () => {
          if (hasChanges) {
            await configMut.mutateAsync(config);
          }
        }}
        disabled={!hasChanges}
        className='w-full'
      >
        {t('dbhost.saveConfig')}
      </Button>
    </div>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    engine: 'postgres' as DbEngine,
    version: '16',
    port: '',
    memory_limit: '512m',
    root_password: '',
    target_node: '',
  });

  const mut = useMutation({
    mutationFn: () =>
      mcpCall('dbhost.create', {
        name: form.name,
        engine: form.engine,
        version: form.version,
        port: form.port ? Number(form.port) : undefined,
        memory_limit: form.memory_limit,
        root_password: form.root_password || undefined,
        target_node: form.target_node || undefined,
      }),
    onSuccess: () => {
      toast.success(t('dbhost.instanceCreated', { name: form.name }));
      onCreated();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const engineCls = (e: DbEngine) =>
    e === 'postgres'
      ? 'border-blue-500/50 text-blue-400 bg-blue-500/10'
      : 'border-amber-500/50 text-amber-400 bg-amber-500/10';

  return (
    <Modal
      open
      onClose={onClose}
      title={t('dbhost.newInstanceTitle')}
      size='md'
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>{t('dbhost.cancel')}</Button>
          <Button
            variant='primary'
            onClick={() => mut.mutate()}
            loading={mut.isPending}
            disabled={!form.name}
          >
            {t('dbhost.createInstance')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <NodeSelector
          value={form.target_node}
          onChange={(v) => setForm((f) => ({ ...f, target_node: v }))}
          label={t('dbhost.targetNode')}
        />
        <div>
          <Label>{t('dbhost.name')}</Label>
          <Input
            size='sm'
            value={form.name}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
              }))}
            placeholder='shared-pg'
          />
          <div className='text-[10px] text-gray-600 mt-1'>
            {t('dbhost.slugHint', { name: form.name || t('dbhost.namePlaceholder') })}
          </div>
        </div>
        <div>
          <Label className='mb-2'>{t('dbhost.engine')}</Label>
          <div className='grid grid-cols-2 gap-2'>
            {(['postgres', 'mariadb'] as DbEngine[]).map((e) => (
              <button
                key={e}
                type='button'
                onClick={() =>
                  setForm((f) => ({ ...f, engine: e, version: DEFAULT_VERSIONS[e][0]! }))}
                className={`py-3 rounded-xl border text-sm font-medium transition-colors ${
                  form.engine === e
                    ? engineCls(e)
                    : 'border-gray-700 text-gray-500 hover:text-white'
                }`}
              >
                {e === 'postgres' ? 'PostgreSQL' : 'MariaDB'}
              </button>
            ))}
          </div>
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>{t('dbhost.version')}</Label>
            <Select
              size='sm'
              value={form.version}
              onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
              options={DEFAULT_VERSIONS[form.engine]}
            />
          </div>
          <div>
            <Label>{t('dbhost.memoryLimit')}</Label>
            <Input
              size='sm'
              value={form.memory_limit}
              onChange={(e) => setForm((f) => ({ ...f, memory_limit: e.target.value }))}
              placeholder='512m'
            />
          </div>
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>{t('dbhost.portAuto')}</Label>
            <Input
              size='sm'
              value={form.port}
              onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
              type='number'
              placeholder='5400-5499'
            />
          </div>
          <div>
            <Label>{t('dbhost.rootPasswordAuto')}</Label>
            <Input
              size='sm'
              value={form.root_password}
              onChange={(e) => setForm((f) => ({ ...f, root_password: e.target.value }))}
              type='password'
              placeholder='auto-generate'
            />
          </div>
        </div>
        {mut.error && (
          <p className='text-xs text-red-400 flex items-center gap-1'>
            <AlertCircle size={12} />
            {(mut.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ── ConnectionPerspectives ───────────────────────────────────────────────────

function ConnectionPerspectives({
  instance,
  engine,
  externalPort,
  rootUser,
  rootPassword,
  credentials,
  showRootPass,
  onToggleRootPass,
}: {
  instance: string;
  engine: DbEngine;
  externalPort: number | null;
  rootUser: string;
  rootPassword: string;
  credentials: { username: string; db_name: string | null; connection_string: string }[];
  showRootPass: boolean;
  onToggleRootPass: () => void;
}) {
  const { t } = useTranslation();
  const internalPort = INTERNAL_PORT[engine];
  const rootDb = engine === 'postgres' ? 'postgres' : 'mysql';
  // Browser ví IP nodu na který se připojil
  const nodeIp = typeof window !== 'undefined' ? window.location.hostname : 'NODE_IP';

  type Perspective = {
    key: 'local' | 'vpn' | 'docker' | 'public';
    label: string;
    description: string;
    host: string;
    port: number;
    note?: string;
    color: string;
  };

  const perspectives: Perspective[] = [
    {
      key: 'local',
      label: t('dbhost.perspLocalLabel'),
      description: t('dbhost.perspLocalDesc', { ip: nodeIp }),
      host: 'localhost',
      port: externalPort ?? 0,
      color: 'text-emerald-400',
    },
    {
      key: 'vpn',
      label: t('dbhost.perspVpnLabel'),
      description: t('dbhost.perspVpnDesc'),
      host: nodeIp,
      port: externalPort ?? 0,
      color: 'text-blue-400',
    },
    {
      key: 'docker',
      label: t('dbhost.perspDockerLabel'),
      description: t('dbhost.perspDockerDesc'),
      host: `dbhost-${instance}`,
      port: internalPort,
      color: 'text-purple-400',
    },
    {
      key: 'public',
      label: t('dbhost.perspPublicLabel'),
      description: t('dbhost.perspPublicDesc'),
      host: '<domain>',
      port: 443,
      note: t('dbhost.perspPublicNote'),
      color: 'text-amber-400',
    },
  ];

  const maskPwd = (s: string) => (showRootPass ? s : s.replace(/:([^:@]+)@/, ':••••••••@'));

  return (
    <div className='space-y-3'>
      {/* Root password — společné pro všechny perspektivy */}
      <div className='grid grid-cols-[100px_1fr] items-center gap-2'>
        <span className='text-[10px] text-gray-500 uppercase tracking-wide'>{t('dbhost.rootPassword')}</span>
        <div className='flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-1.5'>
          <span className='text-xs text-gray-500 font-mono w-20 flex-shrink-0'>{rootUser}</span>
          <code className='text-xs font-mono flex-1 text-amber-300 truncate'>
            {showRootPass ? rootPassword : '••••••••••••••••'}
          </code>
          <button
            type='button'
            onClick={onToggleRootPass}
            className='text-gray-600 hover:text-white'
            title={showRootPass ? t('dbhost.hide') : t('dbhost.show')}
          >
            {showRootPass ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button
            type='button'
            onClick={() => copyText(rootPassword, t('dbhost.copied'))}
            className='text-gray-600 hover:text-white'
            title={t('dbhost.copyPassword')}
          >
            <Copy size={12} />
          </button>
        </div>
      </div>

      {/* 4 perspektivy */}
      <div className='space-y-2'>
        {perspectives.map((p) => {
          const rootStr = buildConnStr(engine, rootUser, rootPassword, p.host, p.port, rootDb);
          return (
            <div
              key={p.key}
              className='border border-gray-800 rounded-xl p-3 bg-gray-900/40'
            >
              <div className='flex items-center justify-between mb-1'>
                <div className='flex items-center gap-2'>
                  <span className={`text-xs font-semibold ${p.color}`}>{p.label}</span>
                  <span className='text-[10px] text-gray-600 font-mono'>
                    {p.host}:{p.port}
                  </span>
                </div>
                {p.note && (
                  <span className='text-[10px] text-amber-500/70 italic'>{p.note}</span>
                )}
              </div>
              <div className='text-[10px] text-gray-500 mb-2'>{p.description}</div>

              {/* Root connection string */}
              <div className='flex items-center gap-2 bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5'>
                <span className='text-[10px] text-gray-500 w-12 flex-shrink-0'>root</span>
                <code className='text-[11px] font-mono flex-1 text-emerald-400 truncate'>
                  {maskPwd(rootStr)}
                </code>
                <button
                  type='button'
                  onClick={() => copyText(rootStr, t('dbhost.copied'))}
                  className='text-gray-600 hover:text-white flex-shrink-0'
                  title={t('dbhost.copy')}
                >
                  <Copy size={12} />
                </button>
              </div>

              {/* Per-user connection strings — přebudované pro tuhle perspektivu */}
              {credentials.length > 0 && (
                <div className='mt-1.5 space-y-1'>
                  {credentials.map((c) => {
                    // Extract password from backend connection_string (= mysql://user:pwd@localhost:port/db)
                    const m = /:\/\/[^:]+:([^@]+)@/.exec(c.connection_string);
                    const pwd = m ? m[1] : '';
                    const dbName = c.db_name ?? rootDb;
                    const userStr = buildConnStr(engine, c.username, pwd, p.host, p.port, dbName);
                    return (
                      <div
                        key={c.username}
                        className='flex items-center gap-2 bg-gray-950 border border-gray-800 rounded-lg px-2 py-1.5'
                      >
                        <span className='text-[10px] text-gray-500 w-12 flex-shrink-0 truncate'>
                          {c.username}
                        </span>
                        <code className='text-[11px] font-mono flex-1 text-gray-300 truncate'>
                          {maskPwd(userStr)}
                        </code>
                        <button
                          type='button'
                          onClick={() => copyText(userStr, t('dbhost.copied'))}
                          className='text-gray-600 hover:text-white flex-shrink-0'
                          title={t('dbhost.copy')}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ChangePortModal ──────────────────────────────────────────────────────────

function ChangePortModal({
  open,
  onClose,
  instance,
  currentPort,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  instance: string;
  currentPort: number | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [newPort, setNewPort] = useState(String(currentPort ?? ''));

  const changeMut = useMutation({
    mutationFn: (port: number) => mcpCall('dbhost.change_port', { instance, port }),
    onSuccess: () => {
      toast.success(t('dbhost.portChanged', { port: newPort }));
      onChanged();
      onClose();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Modal open={open} onClose={onClose} title={t('dbhost.changePortTitle', { instance })}>
      <div className='space-y-3'>
        <div className='text-xs text-gray-400'>
          {t('dbhost.changePortDesc')}
        </div>
        <div>
          <Label>{t('dbhost.newPortRange')}</Label>
          <Input
            size='sm'
            type='number'
            min={5400}
            max={5499}
            value={newPort}
            onChange={(e) => setNewPort(e.target.value)}
            className='font-mono'
          />
        </div>
        <div className='text-[10px] text-amber-500/80 bg-amber-950/30 border border-amber-900/50 rounded-xl p-2'>
          {t('dbhost.changePortWarning')}
        </div>
        <div className='flex justify-end gap-2 pt-2 border-t border-gray-800'>
          <Button variant='secondary' size='sm' onClick={onClose}>{t('dbhost.cancel')}</Button>
          <Button
            variant='primary'
            size='sm'
            onClick={() => {
              const n = parseInt(newPort, 10);
              if (!Number.isInteger(n) || n < 5400 || n > 5499) {
                toast.error(t('dbhost.portMustBeInt'));
                return;
              }
              if (n === currentPort) {
                toast.info(t('dbhost.portUnchanged'));
                return;
              }
              changeMut.mutate(n);
            }}
            loading={changeMut.isPending}
          >
            {t('dbhost.changePortRestart')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DbHostPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dbhost-list'],
    queryFn: () => mcpCall<{ ok: boolean; instances: DbInstance[] }>('dbhost.list'),
    refetchInterval: 30_000,
  });

  const instances = data?.instances ?? [];
  const running = instances.filter((i) => i.container_status === 'running').length;
  const totalDbs = instances.reduce((s, i) => s + (i.database_count ?? 0), 0);
  const totalUsers = instances.reduce((s, i) => s + (i.credential_count ?? 0), 0);
  const selectedInst = instances.find((i) => i.name === selected);

  return (
    <div className='space-y-5'>
      {/* Header */}
      <PageHeader
        title={t('dbhost.pageTitle')}
        description={t('dbhost.pageSubtitle')}
        icon={Database}
        actions={
          <Button variant='primary' icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
            {t('dbhost.newInstance')}
          </Button>
        }
      />

      {/* Stats */}
      <StatsGrid columns={4}>
        <StatCard label={t('dbhost.statInstances')} value={instances.length} icon={Server} />
        <StatCard
          label={t('dbhost.statRunning')}
          value={running}
          icon={Play}
          variant={running > 0 ? 'success' : 'default'}
        />
        <StatCard label={t('dbhost.statDatabases')} value={totalDbs} icon={Database} />
        <StatCard label={t('dbhost.statUsers')} value={totalUsers} icon={Users} />
      </StatsGrid>

      {/* Loading */}
      {isLoading && (
        <div className='grid grid-cols-1 xl:grid-cols-3 gap-5'>
          {[0, 1, 2].map((i) => (
            <div key={i} className='h-32 bg-gray-800/40 rounded-xl animate-pulse' />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && instances.length === 0 && (
        <EmptyState
          icon={<Database size={32} />}
          title={t('dbhost.emptyTitle')}
          description={t('dbhost.emptyDesc')}
          action={
            <Button variant='primary' icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
              {t('dbhost.createFirstInstance')}
            </Button>
          }
        />
      )}

      {/* Main layout */}
      {instances.length > 0 && (
        <div className='grid grid-cols-1 xl:grid-cols-3 gap-5'>
          {/* Instance list */}
          <div className='space-y-2'>
            {instances.map((inst) => (
              <InstanceCard
                key={inst.name}
                inst={inst}
                selected={selected === inst.name}
                onSelect={() => setSelected(inst.name === selected ? null : inst.name)}
                onAction={() => {
                  qc.invalidateQueries({ queryKey: ['dbhost-list'] });
                  qc.invalidateQueries({ queryKey: ['dbhost-status', inst.name] });
                }}
              />
            ))}
          </div>

          {/* Detail panel */}
          <div className='xl:col-span-2'>
            {selected && selectedInst
              ? (
                <InstanceDetail
                  name={selected}
                  engine={selectedInst.engine}
                  onRefresh={() => qc.invalidateQueries({ queryKey: ['dbhost-list'] })}
                />
              )
              : (
                <EmptyState
                  icon={<Server size={28} />}
                  title={t('dbhost.selectInstanceTitle')}
                  description={t('dbhost.selectInstanceDesc')}
                />
              )}
          </div>
        </div>
      )}

      {/* Tabs for instances with counts */}
      {instances.length > 0 && (
        <div className='text-xs text-gray-600 text-right'>
          {t('dbhost.runningOfTotal', { running, total: instances.length })}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['dbhost-list'] });
          }}
        />
      )}
    </div>
  );
}
